import React from "react";
import { View, Text, StyleSheet } from "react-native";
import { useNavigation } from "@react-navigation/native";
import NameInput from "./NameInput";
import SerialNumberInput from "./SerialNumberInput";
import ChargerEmailInput from "./ChargerEmailInput";
import DescriptionInput from "./DescriptionInput";
import RateInput from "./RateInput";
import PeakSelectionPicker from "./PeakSelectionPicker";
import ToggleEnableButton from "./ToggleEnableButton";
import CreateUpdateButtons from "./CreateUpdateButtons";
import RevenueSplitManager from "./RevenueSplitManager";
import LoadingButton from "../LoadingButton";
import { WeekConfiguration } from "../common/utils/peak-off-peak-field";
import PlacesAutocomplete, { Prediction } from "../google-places-autocomplete";
import { isWeb } from "../common/utils/platform.util";

interface ChargerFormProps {
  isEditing: boolean;
  state: {
    form: any;
    errors: Record<string, string>;
    estRates?: { peak: number; off_peak: number };
    returnToOnboarding?: boolean;
  };
  handlers: any;
  isUpdating: boolean;
  isEnabled: boolean;
  toggleEnable: () => void;
  onCreate: () => Promise<void>;
  onUpdate: () => Promise<void>;
  estResidentialPeak: number | null;
  estResidentialOffPeak: number | null;
  rawPeak: string;
  rawOffPeak: string;
  peakSelection: WeekConfiguration;
  isToggleLoading?: boolean;
}

const ChargerForm: React.FC<ChargerFormProps> = ({
  isEditing,
  state,
  handlers,
  isUpdating,
  isEnabled,
  toggleEnable,
  onCreate,
  onUpdate,
  estResidentialPeak,
  estResidentialOffPeak,
  rawPeak,
  rawOffPeak,
  peakSelection,
  isToggleLoading,
}) => {
  const navigation = useNavigation<any>();

  return (
  <View style={styles.container}>
    {isEditing && (
      <ToggleEnableButton 
        isEnabled={isEnabled} 
        onToggle={toggleEnable} 
        isLoading={isToggleLoading}
      />
    )}
    {isEditing && state.form?.uuid && (
      <View style={{ alignItems: "center", marginVertical: isWeb ? 4 : 10, marginBottom: isWeb ? 4 : 20 }}>
        <LoadingButton
          onPress={() => {
            navigation.navigate("ChargerQRCode", {
              chargerId: state.form.uuid,
              chargerName: state.form.name,
            });
          }}
          backgroundColor="#2D6A4F"
        >
          <Text style={{ color: "#FFFFFF", fontSize: 16, fontWeight: "bold", textAlign: "center" }}>
            View QR Code
          </Text>
        </LoadingButton>
      </View>
    )}
    <NameInput
      value={state.form.name}
      error={state.errors.name}
      onChange={handlers.onNameChange}
    />
    <SerialNumberInput value={state.form.unit_serial} />
    <ChargerEmailInput
      value={state.form.charger_email}
      error={state.errors.charger_email}
      onChange={handlers.onChargerEmailChange}
    />
    <PlacesAutocomplete
      value={state.form.full_address}
      error={state.errors.full_address}
      onPlaceSelected={handlers.onAddressSelect}
    />
    <DescriptionInput
      value={state.form.description}
      error={state.errors.description}
      onChange={handlers.onDescriptionChange}
    />
    <RateInput
      label="Peak Rate"
      value={rawPeak}
      onChange={(text: string) =>
        handlers.onKwhChange("price_kwh_peak", text || "")
      }
      est={estResidentialPeak}
    />
    <RateInput
      label="Off-Peak Rate"
      value={rawOffPeak}
      onChange={(text: string) =>
        handlers.onKwhChange("price_kwh_off_peak", text || "")
      }
      est={estResidentialOffPeak}
    />
    <PeakSelectionPicker
      peakSelection={peakSelection}
      onChange={handlers.onPeakChange}
      editChargerStatus={isEditing}
      error={Boolean(state.errors.peakSelection)}
    />
    {isEditing && (
      <RevenueSplitManager
        chargerId={state.form.uuid}
        isEditing={isEditing}
      />
    )}
    <CreateUpdateButtons
      isUpdating={isUpdating}
      isEditing={isEditing}
      onCreate={onCreate}
      onUpdate={onUpdate}
    />
  </View>
  );
};

const styles = StyleSheet.create({
  container: {
    padding: 16,
  },
});

export default ChargerForm;
